import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  PropsWithChildren,
  createContext,
  useContext,
} from 'react'

export interface FileDropType {
  isActive: boolean
  addFiles: (files: FileList) => void
}

export const FileDropContext = createContext<FileDropType | undefined>(undefined)

export const useFileDrop = () => {
  const context = useContext(FileDropContext)

  if (context === undefined) {
    throw new Error('useFileDrop must be used within a FileDrop')
  }

  return context
}

type FileDropProps = PropsWithChildren<{
  fullscreen?: boolean
  onFilesAdded: (files: FileList) => void
}>

export const FileDrop = ({ onFilesAdded, fullscreen, children }: FileDropProps) => {
  const [active, setActive] = useState(false)
  const dropTarget = useRef(null)
  const countRef = useRef(0)
  const target = fullscreen ? document : dropTarget.current
  const context = {
    isActive: active,
    addFiles: onFilesAdded,
  }

  const onDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault()
      e.stopPropagation()

      if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
        onFilesAdded(e.dataTransfer.files)
      }

      countRef.current = 0
      setActive(false)
    },
    [setActive, onFilesAdded]
  )

  const onDragEnter = useCallback(
    (e: DragEvent) => {
      e.preventDefault()
      e.stopPropagation()

      countRef.current++
      if (countRef.current === 1) {
        setActive(true)
      }
    },
    [setActive]
  )

  const onDragLeave = useCallback(
    (e: DragEvent) => {
      e.preventDefault()
      e.stopPropagation()

      countRef.current--
      if (countRef.current === 0) {
        setActive(false)
      }
    },
    [setActive]
  )

  const onDragOver = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  useEffect(() => {
    if (target === null) {
      return
    }

    target.addEventListener('drop', onDrop, false)
    target.addEventListener('dragenter', onDragEnter, false)
    target.addEventListener('dragleave', onDragLeave, false)
    target.addEventListener('dragover', onDragOver, false)
    return () => {
      target.removeEventListener('drop', onDrop)
      target.removeEventListener('dragenter', onDragEnter)
      target.removeEventListener('dragleave', onDragLeave)
      target.removeEventListener('dragover', onDragOver)
    }
  }, [target, onDrop, onDragEnter, onDragLeave, onDragOver])

  return (
    <div role="button" ref={dropTarget}>
      <FileDropContext.Provider value={context}>{children}</FileDropContext.Provider>
    </div>
  )
}
